import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { io, Socket } from 'socket.io-client';
import { apiClient } from '../../services/api';
import AppHeader from '../../components/layout/AppHeader';

const ProjectWorkspace = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [workspace, setWorkspace] = useState<any>(null);
    const [messages, setMessages] = useState<any[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [files, setFiles] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const socketRef = useRef<Socket | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!id) return;

        const fetchWorkspaceData = async () => {
            try {
                setLoading(true);
                // Using collaboration service underlying endpoints
                const COLLAB_URL = import.meta.env.VITE_COLLABORATION_SERVICE_URL || 'https://techunity-ghbh.onrender.com/api/collaboration';

                const [roomRes, msgRes] = await Promise.all([
                    fetch(`${COLLAB_URL}/rooms/${id}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                    }),
                    // Assuming a standard message fetch endpoint based on roomId
                    fetch(`${COLLAB_URL}/messages/${id}?limit=50`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                    }).catch(() => ({ ok: true, json: () => ({ messages: [] }) }))
                ]);

                if (roomRes.ok) {
                    const roomData = await roomRes.json();
                    setWorkspace(roomData);
                } else {
                    navigate('/talent'); // Redirect if unauthorized or not found
                    return;
                }

                if (msgRes && 'ok' in msgRes && msgRes.ok) {
                    const msgData = await msgRes.json();
                    setMessages(msgData.messages || []);
                }

                // Connect Socket
                const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'https://techunity-ghbh.onrender.com';
                socketRef.current = io(SOCKET_URL, {
                    auth: { token: localStorage.getItem('accessToken') },
                    withCredentials: true,
                    transports: ['websocket']
                });

                socketRef.current.on('connect', () => {
                    console.log('Workspace Socket connected');
                    socketRef.current?.emit('room:join', id);
                });

                socketRef.current.on('message:received', (message: any) => {
                    if (message.roomId === id) {
                        setMessages(prev => [...prev, message]);
                        scrollToBottom();
                    }
                });

            } catch (error) {
                console.error('Failed to load workspace:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkspaceData();

        return () => {
            if (socketRef.current) {
                socketRef.current.emit('room:leave', id);
                socketRef.current.disconnect();
            }
        };
    }, [id, navigate]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleSendMessage = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim() || !socketRef.current) return;

        const messageData = {
            roomId: id,
            content: newMessage,
            type: 'text'
        };

        socketRef.current.emit('message:send', messageData);

        // Optimistic UI update
        setMessages(prev => [...prev, {
            _id: Date.now().toString(),
            content: newMessage,
            senderId: currentUser.id || currentUser._id,
            sender: { firstName: currentUser.firstName, lastName: currentUser.lastName },
            createdAt: new Date().toISOString()
        }]);

        setNewMessage('');
        scrollToBottom();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col font-sans selection:bg-premium-gold selection:text-black">
            <AppHeader />

            <main className="flex-grow pt-24 px-6 pb-6 max-w-7xl mx-auto w-full flex flex-col h-screen">

                {/* Workspace Header */}
                <div className="mb-6 flex items-center justify-between border-b border-white/10 pb-6">
                    <div>
                        <h1 className="text-3xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold to-white mb-2">
                            {workspace?.name || 'Project Workspace'}
                        </h1>
                        <p className="text-white/60 text-sm">
                            {workspace?.description || 'Collaborate, share files, and track milestones here.'}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button className="px-4 py-2 border border-premium-gold/30 text-premium-gold rounded-full text-sm font-bold hover:bg-premium-gold/10 transition">
                            Project Settings
                        </button>
                        <button className="px-4 py-2 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark rounded-full text-sm font-bold hover:shadow-[0_0_15px_rgba(212,175,55,0.4)] transition cursor-pointer">
                            Complete Milestone
                        </button>
                    </div>
                </div>

                {/* Workspace Grid */}
                <div className="flex-grow grid grid-cols-1 lg:grid-cols-4 gap-6 overflow-hidden min-h-0">

                    {/* Left Sidebar - Details & Milestones */}
                    <div className="lg:col-span-1 flex flex-col gap-6 overflow-y-auto pr-2 custom-scrollbar">

                        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-md">
                            <h3 className="font-bold text-premium-gold mb-4 uppercase tracking-wider text-xs">Team Members</h3>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 bg-black/40 p-2 rounded-lg border border-white/5">
                                    <div className="w-8 h-8 rounded-full bg-premium-gold text-premium-wood-dark flex items-center justify-center font-bold text-sm">
                                        ME
                                    </div>
                                    <div className="text-sm font-medium">{currentUser.firstName || 'You'} (Employer)</div>
                                </div>
                                <div className="flex items-center gap-3 bg-black/40 p-2 rounded-lg border border-white/5">
                                    <div className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center font-bold text-sm">
                                        T
                                    </div>
                                    <div className="text-sm font-medium">Talent Member</div>
                                    <div className="w-2 h-2 rounded-full bg-green-500 ml-auto"></div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-md flex-grow">
                            <h3 className="font-bold text-premium-gold mb-4 uppercase tracking-wider text-xs">Milestones</h3>
                            <div className="space-y-4 relative before:absolute before:inset-0 before:ml-[11px] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/20 before:to-transparent">

                                <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                    <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-premium-gold bg-black shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_10px_rgba(212,175,55,0.5)] z-10">
                                    </div>
                                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-3 rounded-lg border border-premium-gold/30 bg-premium-gold/5 shadow">
                                        <span className="font-bold text-premium-gold text-xs">Active</span>
                                        <h4 className="text-sm font-bold text-white">Project Kickoff</h4>
                                    </div>
                                </div>

                                <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                                    <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white/20 bg-black shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                                    </div>
                                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-3 rounded-lg border border-white/10 bg-black/40 shadow">
                                        <h4 className="text-sm font-medium text-white/50">First Draft</h4>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Center - Live Chat */}
                    <div className="lg:col-span-2 bg-gradient-to-b from-white/5 to-transparent border border-white/10 rounded-2xl flex flex-col overflow-hidden backdrop-blur-md relative">
                        <div className="p-4 border-b border-white/5 bg-black/40 flex justify-between items-center z-10">
                            <h2 className="font-bold text-white flex items-center gap-2">
                                <span>💬</span> Live Collaboration Chat
                            </h2>
                            <div className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-full border border-green-400/20">
                                Secure Connection
                            </div>
                        </div>

                        <div className="flex-grow p-4 overflow-y-auto custom-scrollbar flex flex-col gap-4 z-10 relative">
                            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)' }}></div>

                            {messages.length === 0 ? (
                                <div className="m-auto text-center text-white/40">
                                    <span className="text-4xl block mb-2">👋</span>
                                    No messages yet. Start the conversation!
                                </div>
                            ) : (
                                messages.map((msg, idx) => {
                                    const isMe = msg.senderId === (currentUser.id || currentUser._id);
                                    const isSystem = msg.type === 'system' || msg.senderId === 'system';

                                    if (isSystem) {
                                        return (
                                            <div key={idx} className="flex justify-center my-2">
                                                <div className="bg-premium-gold/10 border border-premium-gold/20 text-premium-gold text-xs px-4 py-1.5 rounded-full text-center max-w-[80%]">
                                                    {msg.content}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={idx} className={`flex max-w-[85%] ${isMe ? 'ml-auto justify-end' : 'mr-auto justify-start'}`}>
                                            {!isMe && (
                                                <div className="w-8 h-8 rounded-full bg-white/10 mr-2 flex-shrink-0 flex items-center justify-center text-sm font-bold text-white mt-auto">
                                                    {msg.sender?.firstName?.[0] || 'U'}
                                                </div>
                                            )}
                                            <div className={`p-3 rounded-2xl ${isMe
                                                    ? 'bg-premium-gold text-premium-wood-dark rounded-br-sm'
                                                    : 'bg-white/10 text-white rounded-bl-sm border border-white/5'
                                                }`}>
                                                <p className={`text-sm ${isMe ? 'font-medium' : ''}`}>{msg.content}</p>
                                                <span className={`text-[10px] block mt-1 ${isMe ? 'text-black/50 text-right' : 'text-white/40'}`}>
                                                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <form onSubmit={handleSendMessage} className="p-4 bg-black/60 border-t border-white/5 flex gap-2 z-10 relative">
                            <button type="button" className="p-3 text-white/50 hover:text-premium-gold hover:bg-white/10 rounded-xl transition-colors">
                                📎
                            </button>
                            <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type your message..."
                                className="flex-grow bg-white/5 border border-white/10 rounded-xl px-4 text-white placeholder-white/40 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/50 transition-all font-sans"
                            />
                            <button
                                type="submit"
                                disabled={!newMessage.trim()}
                                className="px-6 py-2 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-xl disabled:opacity-50 hover:shadow-[0_0_15px_rgba(212,175,55,0.4)] transition-all"
                            >
                                Send
                            </button>
                        </form>
                    </div>

                    {/* Right Sidebar - Files & Contract */}
                    <div className="lg:col-span-1 flex flex-col gap-6 overflow-y-auto pl-2 custom-scrollbar">

                        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-md flex-grow">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-premium-gold uppercase tracking-wider text-xs">Shared Files</h3>
                                <button className="text-white/50 hover:text-white text-sm">Upload</button>
                            </div>

                            {files.length === 0 ? (
                                <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center flex flex-col items-center justify-center h-[200px] hover:border-premium-gold/30 hover:bg-white/5 transition-colors cursor-pointer group">
                                    <span className="text-3xl text-white/20 mb-2 group-hover:text-premium-gold/60 transition-colors">📁</span>
                                    <span className="text-sm text-white/40 group-hover:text-white/70">Drag files or click to upload</span>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {/* File placeholders */}
                                </div>
                            )}
                        </div>

                        <div className="bg-gradient-to-br from-premium-wood-light/20 to-transparent border border-premium-gold/20 rounded-2xl p-5 backdrop-blur-md">
                            <h3 className="font-bold text-premium-gold mb-4 uppercase tracking-wider text-xs flex items-center gap-2">
                                <span>📄</span> Contract Summary
                            </h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between border-b border-white/5 pb-2">
                                    <span className="text-white/50">Status</span>
                                    <span className="text-green-400 font-bold">Active</span>
                                </div>
                                <div className="flex justify-between border-b border-white/5 pb-2">
                                    <span className="text-white/50">Type</span>
                                    <span className="text-white font-medium">Fixed Price</span>
                                </div>
                                <div className="flex justify-between border-b border-white/5 pb-2">
                                    <span className="text-white/50">Budget</span>
                                    <span className="text-white font-bold font-serif">$1,500</span>
                                </div>
                                <div className="flex justify-between pt-1">
                                    <span className="text-white/50">Escrow Balance</span>
                                    <span className="text-premium-gold font-bold font-serif">$1,500</span>
                                </div>
                            </div>
                            <button className="w-full mt-4 py-2 border border-premium-gold/30 text-premium-gold hover:bg-premium-gold hover:text-premium-wood-dark transition-colors font-bold rounded-lg text-sm bg-black/40">
                                View Full Contract
                            </button>
                        </div>

                    </div>

                </div>
            </main>
        </div>
    );
};

export default ProjectWorkspace;
